import type { QueueJoinOfferOption } from '../../reservations/queue-join/queue-join-types';
import { useDetailAvailabilityData } from './use-detail-availability-data';
import { useDetailInspectCell } from './use-detail-inspect-cell';

interface UseDetailAvailabilityOptions {
  unitId: number;
  city: string | undefined;
  /** The availability calendar is a feature flag the permission plug does not carry, so the app resolves it. */
  canViewCalendar?: boolean;
  /** Draft offers the waitlist identity picker can attach a join to; offers are not part of @ooh/screens yet. */
  queueOfferOptions?: QueueJoinOfferOption[];
}

/**
 * Availability calendar wiring for the media unit detail view: data, window and granularity
 * state, and the inspect popover (waitlist join, upgrade, close sale, cancel, force release).
 * Spread `viewProps` into `MediaUnitDetailView`; `periods` and `entriesGrid` are for callers
 * that add their own selection flow on top.
 */
export function useDetailAvailability({
  unitId,
  city,
  canViewCalendar = true,
  queueOfferOptions,
}: UseDetailAvailabilityOptions) {
  const data = useDetailAvailabilityData(unitId, canViewCalendar);
  const renderCellContent = useDetailInspectCell({
    unitId,
    city,
    periods: data.periods,
    entriesGrid: data.entriesGrid,
    occupancyByRowId: data.occupancyByRowId,
    queueOfferOptions,
  });

  return {
    periods: data.periods,
    entriesGrid: data.entriesGrid,
    viewProps: {
      showAvailability: true,
      disableInternalPopover: true,
      occupancyByRowId: data.occupancyByRowId,
      entriesGrid: data.entriesGrid,
      windowStartDate: data.windowStartDate,
      onPrevWindow: data.onPrevWindow,
      onNextWindow: data.onNextWindow,
      granularity: data.granularity,
      onGranularityChange: data.onGranularityChange,
      renderCellContent,
    },
  };
}
